// EXPERIMENT #171270395
import React from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';

import { Formik } from 'formik';

import Button from 'components/button';
import { sessionStorageGetItem } from 'entrypoints/storage_helpers';
import { getAnswersFromStorage, storeAnswerToStorage } from '../storage_helpers';
import {
  trackQuestionAnswered,
  trackBackButtonClicked,
  trackIntroductionGetStartedClicked,
} from '../tracking_helpers';
import SurveyQuestion from './survey_question';
import QUESTIONS_INDUSTRY_IDENTIFICATION from '../constants/configurations_industry_identification_exp';
import QUESTIONS_DEFAULT from '../constants/configurations';
import IntroductionScreen from './introduction_screen';
import Header from './survey_header';
import ProcessingScreen from './processing_screen';

class SurveyManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentQuestion: 'RUN_PAYROLL',
      showProcessingScreen: false,
      hasUserStartedSurvey: false,
      showIntroductionScreen: this.props.showIntroductionScreen,
    };
  }

  getQuestions = () => {
    if (this.props.withIndustryIdentification) {
      return QUESTIONS_INDUSTRY_IDENTIFICATION; // EXPERIMENT GWTH-1555
    }
    return QUESTIONS_DEFAULT;
  };

  QUESTIONS = this.getQuestions();

  PROCESSING_SCREEN_DELAY = 1000;

  setNextState = (nextQuestion, showProcessingScreenWithDelay) => {
    if (!nextQuestion) {
      if (showProcessingScreenWithDelay) {
        this.setState({ showProcessingScreen: true });
        setTimeout(() => {
          this.props.onSurveyFinish();
        }, this.PROCESSING_SCREEN_DELAY);
      } else {
        this.props.onSurveyFinish();
      }
    }

    this.setState({ currentQuestion: nextQuestion });
  };

  getNextState = (selectedAnswer, currentQuestion) => {
    const nextQuestionOptions = this.QUESTIONS[currentQuestion].next;

    let nextQuestionKey = nextQuestionOptions.default;
    if (selectedAnswer in nextQuestionOptions) {
      nextQuestionKey = nextQuestionOptions[selectedAnswer];
    }

    return nextQuestionKey;
  };

  getNextStateFromSession() {
    const savedAnswers = getAnswersFromStorage();
    let currentQuestion = 'RUN_PAYROLL';

    while (currentQuestion && savedAnswers[currentQuestion]) {
      currentQuestion = this.getNextState(savedAnswers[currentQuestion], currentQuestion);
    }
    return currentQuestion;
  }

  updateQuestionState(showProcessingScreenWithDelay) {
    const currentQuestionBasedOnSession = this.getNextStateFromSession();
    if (this.state.currentQuestion !== currentQuestionBasedOnSession) {
      this.setNextState(currentQuestionBasedOnSession, showProcessingScreenWithDelay);
    }
  }

  updateIntroductionScreenState() {
    const savedAnswers = getAnswersFromStorage();
    if (
      this.state.showIntroductionScreen !==
      (this.props.showIntroductionScreen && !savedAnswers.hasInteractedWithSurvey)
    ) {
      this.setState({
        showIntroductionScreen:
          this.props.showIntroductionScreen && !savedAnswers.hasInteractedWithSurvey,
      });
    }
  }

  componentDidMount() {
    const savedAnswers = getAnswersFromStorage();
    this.setState({ hasUserStartedSurvey: Boolean(savedAnswers.RUN_PAYROLL) });
    this.updateQuestionState(false);
  }

  componentDidUpdate() {
    this.updateQuestionState(true);
    this.updateIntroductionScreenState();
  }

  render() {
    const {
      currentQuestion,
      showIntroductionScreen,
      showProcessingScreen,
      hasUserStartedSurvey,
    } = this.state;

    const onAnswerSelected = () => {
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      this.setState({ hasUserStartedSurvey: false });
      this.updateQuestionState(true);
    };

    const onBackClick = () => {
      const prevQuestionKey = this.QUESTIONS[currentQuestion].prev;
      trackBackButtonClicked(currentQuestion, sessionStorageGetItem('surveySource'));

      if (prevQuestionKey) {
        this.setState({ currentQuestion: prevQuestionKey });
        storeAnswerToStorage(prevQuestionKey, null);
      }

      this.setState({ hasUserStartedSurvey: false });
    };

    const onOtherAnswerSelected = () => {
      const answerValue = this.QUESTIONS[currentQuestion].other_answer.key;
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      storeAnswerToStorage(currentQuestion, answerValue);
      trackQuestionAnswered(currentQuestion, answerValue, sessionStorageGetItem('surveySource'));
      onAnswerSelected();
    };

    const onIntroductionGetStartedClicked = () => {
      storeAnswerToStorage('hasInteractedWithSurvey', true);
      trackIntroductionGetStartedClicked();
      this.updateIntroductionScreenState();
    };

    return (
      <Formik>
        <div className="survey-manager">
          {showIntroductionScreen && (
            <IntroductionScreen
              onClick={onIntroductionGetStartedClicked}
              withSeeDemoLink={this.props.withSeeDemoLink}
            />
          )}
          {showProcessingScreen && <ProcessingScreen />}
          {currentQuestion && !showIntroductionScreen && (
            <div className="text-left">
              <Header
                hasUserStartedSurvey={hasUserStartedSurvey}
                headerText={this.QUESTIONS[currentQuestion].header}
              />
              <SurveyQuestion
                questionKey={currentQuestion}
                question={this.QUESTIONS[currentQuestion]}
                onAnswerSelected={onAnswerSelected}
                withIndustryIdentification={this.props.withIndustryIdentification}
              />
              <div>
                {this.QUESTIONS[currentQuestion].prev && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-6 text-left lp-survey-back-link"
                    label="Back"
                    onClick={onBackClick}
                  />
                )}
                {this.QUESTIONS[currentQuestion].other_answer && (
                  <Button
                    className="btn-link c-kale-500 width-100 col-6 text-right"
                    label={this.QUESTIONS[currentQuestion].other_answer.text}
                    onClick={onOtherAnswerSelected}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Formik>
    );
  }
}

SurveyManager.propTypes = {
  onSurveyFinish: PropTypes.func.isRequired,
  showIntroductionScreen: PropTypes.bool,
  withIndustryIdentification: PropTypes.bool,
  withSeeDemoLink: PropTypes.bool,
};

SurveyManager.defaultProps = {
  showIntroductionScreen: false,
  withIndustryIdentification: false,
  withSeeDemoLink: false,
};

export default hot(module)(SurveyManager);
